import type { LocalIfcObservation } from "../integration/local-ifc";
import type { AnonymousFixture, FixtureElement } from "./types";

export interface LocalIfcQualityIssue {
  readonly source: "geometry" | "express";
  readonly severity: string;
  readonly message: string;
  readonly raw: Readonly<Record<string, unknown>>;
}

export interface LocalIfcProjection {
  readonly fixture: AnonymousFixture;
  readonly quality: {
    readonly state: "partial";
    readonly issues: readonly LocalIfcQualityIssue[];
    readonly skippedCount: number;
    readonly unsupportedProjectionCount: number;
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function items(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : [];
}

function finite(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function point2(value: unknown): readonly [number, number] | null {
  if (!Array.isArray(value) || value.length < 2) return null;
  const x = finite(value[0]);
  const y = finite(value[1]);
  return x === null || y === null ? null : [x, y];
}

function text(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() !== "" ? value : fallback;
}

function sourceEvidence(record: unknown, projection: string): Readonly<Record<string, unknown>> {
  return { sourceRecord: record, projection };
}

function propertyOverlayByElementId(plan: Record<string, unknown>): ReadonlyMap<string, Readonly<Record<string, unknown>>> {
  const result = new Map<string, Record<string, unknown>>();
  for (const rawAssoc of items(plan.properties)) {
    if (!isRecord(rawAssoc) || typeof rawAssoc.element_id !== "string" || rawAssoc.element_id === "") continue;
    const values: Record<string, unknown> = {};
    const ambiguous = new Set<string>();
    for (const rawSet of items(rawAssoc.sets)) {
      if (!isRecord(rawSet)) continue;
      for (const rawProperty of items(rawSet.props)) {
        if (!isRecord(rawProperty) || typeof rawProperty.name !== "string" || rawProperty.name === "") continue;
        if (Object.prototype.hasOwnProperty.call(values, rawProperty.name)) ambiguous.add(rawProperty.name);
        else values[rawProperty.name] = structuredClone(rawProperty.value);
      }
    }
    for (const name of ambiguous) delete values[name];
    result.set(rawAssoc.element_id, values);
  }
  return result;
}

function propertyOverlayForRecord(
  record: Record<string, unknown>,
  propertyOverlay: ReadonlyMap<string, Readonly<Record<string, unknown>>>
): Readonly<Record<string, unknown>> {
  const matches = new Set([record.id, record.guid]
    .filter((value): value is string => typeof value === "string" && value !== "")
    .map((value) => propertyOverlay.get(value))
    .filter((value): value is Readonly<Record<string, unknown>> => value !== undefined));
  return matches.size === 1 ? [...matches][0] : {};
}

function spatialElement(
  id: string,
  label: string,
  type: string,
  record: Record<string, unknown>,
  properties: Readonly<Record<string, unknown>>
): FixtureElement {
  return {
    id,
    label,
    type,
    properties,
    quantities: {},
    classification: null,
    geometry: null,
    editable: false,
    unsupportedReason: `${type} is retained for inspection but has no renderable geometry in the local plan projection.`,
    unknown: sourceEvidence(record, "spatial-record")
  };
}

function wallElement(
  floorIndex: number,
  wallIndex: number,
  floor: Record<string, unknown>,
  wall: Record<string, unknown>,
  propertyOverlay: ReadonlyMap<string, Readonly<Record<string, unknown>>>
): FixtureElement {
  const id = `ifc-floor-${floorIndex + 1}-wall-${wallIndex + 1}`;
  const label = text(wall.id, `Wall ${wallIndex + 1}`);
  const start = point2(wall.start);
  const end = point2(wall.end);
  const thicknessMm = finite(wall.thickness_mm);
  const heightMm = finite(wall.height_mm);
  const baseMm = finite(floor.base_elevation_mm) ?? 0;
  const uniqueOverlay = propertyOverlayForRecord(wall, propertyOverlay);
  const geometryValid =
    start !== null &&
    end !== null &&
    thicknessMm !== null &&
    heightMm !== null &&
    thicknessMm > 0 &&
    heightMm > 0 &&
    Math.hypot(end[0] - start[0], end[1] - start[1]) > 0;
  const lengthMm = start === null || end === null ? null : Math.hypot(end[0] - start[0], end[1] - start[1]);

  return {
    id,
    label,
    type: "Wall",
    properties: {
      GlobalId: typeof wall.guid === "string" ? wall.guid : null,
      FloorLevel: finite(floor.level),
      Structural: typeof wall.structural_wall === "boolean" ? wall.structural_wall : null,
      AssemblyId: typeof wall.assembly_id === "string" ? wall.assembly_id : null,
      TypeId: typeof wall.type_id === "string" ? wall.type_id : null,
      ...uniqueOverlay
    },
    quantities:
      lengthMm === null || thicknessMm === null || heightMm === null
        ? {}
        : {
            Length: lengthMm / 1_000,
            Area: (lengthMm * heightMm) / 1_000_000,
            Volume: (lengthMm * heightMm * thicknessMm) / 1_000_000_000
          },
    classification: null,
    geometry: geometryValid
      ? {
          kind: "box",
          position: [
            (start![0] + end![0]) / 2_000,
            baseMm / 1_000,
            (start![1] + end![1]) / 2_000
          ],
          size: [lengthMm! / 1_000, heightMm! / 1_000, thicknessMm! / 1_000],
          rotationY: -Math.atan2(end![1] - start![1], end![0] - start![0])
        }
      : null,
    editable: geometryValid,
    ...(geometryValid ? {} : { unsupportedReason: "Wall geometry is incomplete or outside the supported plan-derived box projection." }),
    unknown: sourceEvidence(wall, "wall-plan-record")
  };
}

function collectionType(collection: string): string {
  const singular = collection.endsWith("ies")
    ? `${collection.slice(0, -3)}y`
    : collection.endsWith("s")
      ? collection.slice(0, -1)
      : collection;
  return singular
    .split("_")
    .filter((part) => part !== "")
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join("");
}

function collectionElement(
  scope: string,
  collection: string,
  itemIndex: number,
  value: unknown,
  floorIndex: number | null = null,
  propertyOverlay: ReadonlyMap<string, Readonly<Record<string, unknown>>> = new Map()
): FixtureElement {
  const record = isRecord(value) ? value : {};
  const type = collectionType(collection);
  const collectionId = collection.replace(/[^a-z0-9_-]/gi, "-").toLowerCase();
  const uniqueOverlay = propertyOverlayForRecord(record, propertyOverlay);
  const editable = Object.keys(uniqueOverlay).length > 0;
  return {
    id: `ifc-${scope}-${collectionId}-${itemIndex + 1}`,
    label: text(record.id, `${type} ${itemIndex + 1}`),
    type,
    properties: {
      Collection: collection,
      // Not rendered as plan-derived geometry here (this projection only ever built boxes for
      // walls), but retaining the GlobalId the same way the wall path already does (`wall.guid`,
      // below) lets a later, separate mesh-geometry merge (src/domain/ifc-mesh-import.ts) match this
      // record by identity even though it was never geometrized by this importer. Omitting it
      // silently capped every non-wall plan collection (Column, Floor, Covering, Fixture, ...) out
      // of ever being renderable at all, regardless of what geometry a mesh engine could separately
      // resolve. Structured plan records key this `guid`, not `global_id` (that field name belongs
      // only to skippedElement()'s different, importer-diagnostic record shape).
      ...(typeof record.guid === "string" && record.guid !== "" ? { GlobalId: record.guid } : {}),
      ...(floorIndex === null ? {} : { FloorIndex: floorIndex + 1 }),
      ValueKind: value === null ? "null" : Array.isArray(value) ? "array" : typeof value,
      ...uniqueOverlay
    },
    quantities: {},
    classification: null,
    geometry: null,
    editable,
    ...(editable ? {} : { unsupportedReason: `${type} is retained as plan evidence, but this projection increment only renders supported plan geometry.` }),
    unknown: sourceEvidence(value, `${scope}.${collection}-unsupported-projection`)
  };
}

function skippedElement(value: unknown, index: number): FixtureElement {
  const record = isRecord(value) ? value : {};
  const ifcType = text(record.ifc_type, "IFC entity");
  const reason = text(record.reason, "Importer reported an unsupported entity.");
  return {
    id: `ifc-skipped-${index + 1}`,
    label: `${ifcType} · ${text(record.name, `Skipped ${index + 1}`)}`,
    type: "Skipped",
    properties: {
      IfcType: ifcType,
      ExpressId: finite(record.express_id),
      GlobalId: typeof record.global_id === "string" ? record.global_id : null,
      Reason: reason
    },
    quantities: {},
    classification: null,
    geometry: null,
    editable: false,
    unsupportedReason: reason,
    unknown: sourceEvidence(value, "importer-skipped-record")
  };
}

function qualityIssues(report: Record<string, unknown>): readonly LocalIfcQualityIssue[] {
  const normalize = (source: "geometry" | "express", value: unknown): LocalIfcQualityIssue[] =>
    items(value).map((item) => {
      const raw = isRecord(item) ? item : { rawValue: structuredClone(item) };
      return {
        source,
        severity: text(raw.severity, "unknown"),
        message: text(raw.message, "Importer issue did not include a message."),
        raw
      };
    });
  return [...normalize("geometry", report.geometryIssues), ...normalize("express", report.expressIssues)];
}

function retainArrayCollections(
  elements: FixtureElement[],
  container: Record<string, unknown>,
  scope: string,
  excluded: ReadonlySet<string>,
  floorIndex: number | null = null,
  propertyOverlay: ReadonlyMap<string, Readonly<Record<string, unknown>>> = new Map()
): number {
  let retained = 0;
  for (const collection of Object.keys(container).sort()) {
    if (excluded.has(collection) || !Array.isArray(container[collection])) continue;
    items(container[collection]).forEach((value, itemIndex) => {
      elements.push(collectionElement(scope, collection, itemIndex, value, floorIndex, propertyOverlay));
      retained += 1;
    });
  }
  return retained;
}

export function projectLocalIfcObservation(observation: LocalIfcObservation): LocalIfcProjection {
  if (observation.state !== "observed" || !isRecord(observation.raw)) {
    throw new TypeError("Only an observed local IFC result can be projected.");
  }
  const plan = observation.raw.plan;
  if (!isRecord(plan)) throw new TypeError("Observed local IFC result does not contain an object plan.");
  if (plan.units !== "mm") throw new RangeError(`Unsupported local IFC plan units: ${String(plan.units)}`);
  const report = isRecord(observation.raw.report) ? observation.raw.report : {};
  const propertyOverlay = propertyOverlayByElementId(plan);
  const elements: FixtureElement[] = [];
  let unsupportedProjectionCount = 0;

  items(plan.buildings).forEach((value, index) => {
    if (!isRecord(value)) {
      unsupportedProjectionCount += 1;
      elements.push(collectionElement("plan", "buildings", index, value));
      return;
    }
    elements.push(spatialElement(
      `ifc-building-${index + 1}`,
      text(value.name, text(value.id, `Building ${index + 1}`)),
      "Building",
      value,
      { SourceId: typeof value.id === "string" ? value.id : null }
    ));
    unsupportedProjectionCount += 1;
  });

  unsupportedProjectionCount += retainArrayCollections(elements, plan, "plan", new Set(["buildings", "floors"]), null, propertyOverlay);
  if (isRecord(plan.systems)) {
    unsupportedProjectionCount += retainArrayCollections(elements, plan.systems, "plan-systems", new Set(), null, propertyOverlay);
  }
  if (isRecord(plan.site)) {
    unsupportedProjectionCount += retainArrayCollections(elements, plan.site, "plan-site", new Set(), null, propertyOverlay);
  }

  items(plan.floors).forEach((value, floorIndex) => {
    if (!isRecord(value)) {
      unsupportedProjectionCount += 1;
      elements.push(collectionElement("plan", "floors", floorIndex, value));
      return;
    }
    const floor = value;
    const level = finite(floor.level);
    elements.push(
      spatialElement(
        `ifc-floor-${floorIndex + 1}`,
        level === null ? `Floor ${floorIndex + 1}` : `Floor ${level}`,
        "Floor",
        floor,
        { Level: level, BaseElevationMm: finite(floor.base_elevation_mm) }
      )
    );
    unsupportedProjectionCount += 1;
    items(floor.walls).forEach((wall, wallIndex) => {
      if (isRecord(wall)) {
        const element = wallElement(floorIndex, wallIndex, floor, wall, propertyOverlay);
        if (element.geometry === null) unsupportedProjectionCount += 1;
        elements.push(element);
      } else {
        unsupportedProjectionCount += 1;
        elements.push(collectionElement(`floor-${floorIndex + 1}`, "walls", wallIndex, wall, floorIndex));
      }
    });
    unsupportedProjectionCount += retainArrayCollections(
      elements,
      floor,
      `floor-${floorIndex + 1}`,
      new Set(["walls"]),
      floorIndex,
      propertyOverlay
    );
    if (isRecord(floor.mep)) {
      unsupportedProjectionCount += retainArrayCollections(
        elements,
        floor.mep,
        `floor-${floorIndex + 1}-mep`,
        new Set(),
        floorIndex,
        propertyOverlay
      );
    }
  });

  const skipped = items(report.skipped);
  skipped.forEach((record, index) => elements.push(skippedElement(record, index)));
  unsupportedProjectionCount += skipped.length;

  return {
    fixture: {
      id: `local-ifc:${observation.source.sha256}`,
      label: observation.source.filename,
      elements
    },
    quality: {
      state: "partial",
      issues: qualityIssues(report),
      skippedCount: skipped.length,
      unsupportedProjectionCount
    }
  };
}
